import dayjs from 'dayjs';
import { AxiosResponse } from 'axios';
import { EidProvider, Country, Language } from '@/types';
import { Page } from '@/common';
import { client } from '@/client';

export enum AuthMethod {
  SSN = 'SSN',
  SMS = 'SMS',
  EMAIL = 'EMAIL',
  CHAT = 'CHAT',
}

export enum CallInitiator {
  USER = 'USER',
  RP = 'RP',
}

export type StartTeleIdRequest = {
  provider: EidProvider;
  companySignatoryLookup?: {
    country: Country;
    orgNumber: string;
  };
  language?: Language;
  requestAddress?: boolean;
} & (
  | {
      method: AuthMethod.SSN;
      ssn: string;
      country: Country;
      callInitiator: CallInitiator;
    }
  | {
      method: AuthMethod.SMS;
      phone: string;
      country: Country;
    }
  | {
      method: AuthMethod.EMAIL;
      email: string;
    }
  | {
      method: AuthMethod.CHAT;
    }
);

export type EnrichTeleIdRequest = {
  orgNumber?: string;
  requestAddress?: boolean;
};

export type TeleIdLogResponse = {
  id: string;
  ssn: string;
  country: Country;
  dateTime: number; // UNIX timestamp (seconds)
  autodeleteDate: string; // yyyy-MM-dd
  agentName: string;
  bankIdToken: string;
  endUserName: string;
  certStartDate: number; // UNIX timestamp (seconds)
  bankIdNumber: string;
  address: string;
  phone: string;
  companySignatoryOrgNumber: string;
  companySignatoryText: string;
};

export type TeleIdDeliveryStatus = 'PENDING' | 'FAILED' | 'DELIVERED';

export type TeleIdStatusResponse = {
  id: string;
  method: AuthMethod;
  provider: EidProvider;
} & (
  | {
      status: 'PENDING';
      deliveryStatus?: TeleIdDeliveryStatus;
      bindingMessage?: string;
      url?: string;
    }
  | {
      status: 'COMPLETED';
      name: string;
      ssn?: string;
      country: Country;
      certStartDate?: string; // yyyy-MM-dd
      companySignatoryText?: string;
      address?: string;
    }
  | {
      status: 'FAILED';
      error: string;
    }
);

export type TeleIdAuthStatusResponse = {
  id: string;
  method: AuthMethod;
  provider: EidProvider;
  language: Language;
  companyName: string;
  companyLogo: string;
} & (
  | {
      status: 'PENDING';
      agentName?: string;
      displayText?: string;
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
    }
  | {
      status: 'FAILED';
      error: string;
    }
);

export async function getTeleIdLogs(
  params?: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: 'id' | 'dateTime' | 'ssn' | 'endUserName' | 'agentName';
    sortDirection?: 'ascending' | 'descending';
    fromDate?: Date;
    toDate?: Date;
    ssn?: string;
  },
  signal?: AbortSignal,
): Promise<Page<TeleIdLogResponse>> {
  return client
    .get<never, AxiosResponse<Page<TeleIdLogResponse>>>('/teleid', {
      params: {
        ...params,
        fromDate: params?.fromDate
          ? dayjs(params.fromDate).format('YYYY-MM-DD')
          : null,
        toDate: params?.toDate
          ? dayjs(params.toDate).format('YYYY-MM-DD')
          : null,
      },
      signal,
    })
    .then((response) => response.data);
}

export async function startTeleId(
  request: StartTeleIdRequest,
  signal?: AbortSignal,
): Promise<Extract<TeleIdStatusResponse, { status: 'PENDING' }>> {
  return client
    .post<
      StartTeleIdRequest,
      AxiosResponse<Extract<TeleIdStatusResponse, { status: 'PENDING' }>>
    >('/teleid', request, { signal })
    .then((response) => response.data);
}

export async function cancelTeleId(
  id: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .delete<void, AxiosResponse<void>>(`/teleid/${id}`, {
      signal,
    })
    .then((response) => response.data);
}

export async function getTeleIdStatus(
  id: string,
  signal?: AbortSignal,
): Promise<TeleIdStatusResponse> {
  return client
    .get<never, AxiosResponse<TeleIdStatusResponse>>(`/teleid/${id}`, {
      signal,
    })
    .then((response) => response.data);
}

export async function startTeleIdAuth(
  id: string,
  signal?: AbortSignal,
): Promise<Extract<TeleIdAuthStatusResponse, { status: 'PENDING' }>> {
  return client
    .post<
      never,
      AxiosResponse<Extract<TeleIdAuthStatusResponse, { status: 'PENDING' }>>
    >(`/teleid/${id}/auth`, { signal })
    .then((response) => response.data);
}

export async function getTeleIdAuthStatus(
  id: string,
  signal?: AbortSignal,
): Promise<TeleIdAuthStatusResponse> {
  return client
    .get<never, AxiosResponse<TeleIdAuthStatusResponse>>(`/teleid/${id}/auth`, {
      signal,
    })
    .then((response) => response.data);
}

export async function enrichTeleId(
  id: string,
  request: EnrichTeleIdRequest,
  signal?: AbortSignal,
): Promise<Extract<TeleIdStatusResponse, { status: 'COMPLETED' }>> {
  return client
    .post<
      StartTeleIdRequest,
      AxiosResponse<Extract<TeleIdStatusResponse, { status: 'COMPLETED' }>>
    >(`/teleid/${id}/enrich`, request, { signal })
    .then((response) => response.data);
}
